import { Distribution, DistributionParam, Distributions } from "@custom-types/graphs"
import { BaseGraphZipNodeProperties, DfCpd } from "@store/services/converter/types"
import { isNumericString } from "@utils/string"

type DfCpdValues = BaseGraphZipNodeProperties["df_cpd"][keyof BaseGraphZipNodeProperties["df_cpd"]]
export type DfCpdRow = ReturnType<typeof dfCpdToRows>[number]

export const dfCpdToRows = (dfCpd: BaseGraphZipNodeProperties["df_cpd"]) => {
  const rows: Record<string, DfCpdValues>[] = []
  const numberOfRows = dfCpd.distribution?.length ?? 0

  Object.keys(dfCpd).forEach((key) => {
    for (let i = 0; i < numberOfRows; i++) {
      const row = rows[i]

      if (!row) rows[i] = { [key]: dfCpd[key][i] }
      else row[key] = dfCpd[key][i]
    }
  })

  return rows
}

export type DfCpdStrictRow = Partial<Record<DistributionParam, string | null>> & {
  distribution?: Distributions | (string & {})
  expression?: string
}

export function dfCpdToStrictRows(dfCpd: DfCpd): DfCpdStrictRow[] {
  const numberOfRows = dfCpd.distribution?.length ?? 0
  const rows: DfCpdStrictRow[] = Array.from({ length: numberOfRows }, () => ({}))

  for (const key of Object.keys(dfCpd)) {
    if (key === "distribution") {
      dfCpd.distribution?.forEach((value, i) => {
        const row = rows[i]
        if (row) row.distribution = value
      })
    } else if (key === "expression") {
      dfCpd.expression?.forEach((value, i) => {
        const row = rows[i]
        if (row) row.expression = value
      })
    } else {
      const param = key as DistributionParam
      dfCpd[param]?.forEach((value, i) => {
        const row = rows[i]
        if (row) row[param] = value
      })
    }
  }

  return rows
}

export const rowsToDfCpd = (rows: Record<string, DfCpdValues>[]) => {
  const dfCpd = {} as BaseGraphZipNodeProperties["df_cpd"]

  rows.forEach((row, i) => {
    Object.keys(row).forEach((key) => {
      if (!dfCpd[key]) dfCpd[key] = []

      dfCpd[key][i] = row[key]
    })
  })

  return dfCpd
}

export const getDistributionRowPossibleValues = (row: DfCpdRow): string[] => {
  // agreed to not support hypergeometric distribution if not needed
  switch (row.distribution) {
    case Distribution.Binomial:
      return getBinomialDistributionRowPossibleValues(row)
    case Distribution.Geometric:
      return getGeometricDistributionRowPossibleValues(row)
    case Distribution.Logseries:
      return getLogseriesDistributionRowPossibleValues(row)
    case Distribution.Poisson:
      return getPoissonDistributionRowPossibleValues(row)
    default:
      return []
  }
}

const getBinomialDistributionRowPossibleValues = (row: DfCpdRow) => {
  const values: string[] = []

  const n = isNumericString(row[DistributionParam.n]) ? Number(row[DistributionParam.n]) : 1
  const loc = isNumericString(row[DistributionParam.loc]) ? Number(row[DistributionParam.loc]) : 0

  const lowerBound = isNumericString(row[DistributionParam.lbound])
    ? Number(row[DistributionParam.lbound])
    : loc
  const upperBound = isNumericString(row[DistributionParam.ubound])
    ? Number(row[DistributionParam.ubound])
    : n + loc

  const startIndex = Math.max(Math.floor(loc), Math.floor(lowerBound))
  const endIndex = Math.min(Math.floor(n + loc), Math.floor(upperBound))

  for (let index = startIndex; index <= endIndex; index++) {
    values.push(index.toString())
  }

  return values
}

/**
 * States of a Geometric or Logseries node: `[max(loc + 1, lbound), ubound]`, both ends
 * inclusive. Support starts at `loc + 1`, and `lbound` can only raise the floor, never
 * lower it.
 *
 * Both distributions have infinite support, so without `ubound` there is nothing to
 * enumerate and the node has no states. `ubound` is mandatory once the node has a
 * child; that rule is enforced elsewhere as a validation error.
 */
const getGeometricDistributionRowPossibleValues = (row: DfCpdRow) => {
  const values: string[] = []
  if (!isNumericString(row[DistributionParam.ubound])) return values

  const startIndex =
    Math.floor(
      isNumericString(row[DistributionParam.loc]) ? Number(row[DistributionParam.loc]) : 0,
    ) + 1

  const lowerBound = isNumericString(row[DistributionParam.lbound])
    ? Number(row[DistributionParam.lbound])
    : startIndex
  const upperBound = Number(row[DistributionParam.ubound])

  for (let index = startIndex; index <= upperBound; index++) {
    if (index < lowerBound) continue

    values.push(index.toString())
  }

  return values
}

const getLogseriesDistributionRowPossibleValues = (row: DfCpdRow) => {
  // works exactly the same as geometric distribution
  return getGeometricDistributionRowPossibleValues(row)
}

/**
 * States of a Poisson node: `[max(loc, lbound), ubound]`, both ends inclusive. Unlike
 * Geometric and Logseries its support starts at `loc` itself, not `loc + 1`; that offset
 * is the only difference between them.
 *
 * Poisson support is also infinite, so without `ubound` there is nothing to enumerate and
 * the node has no possible values. `ubound` is mandatory once the node has a child; that
 * rule is enforced elsewhere as a validation error.
 */
const getPoissonDistributionRowPossibleValues = (row: DfCpdRow) => {
  const values: string[] = []

  if (!isNumericString(row[DistributionParam.ubound])) return values

  const startIndex = Math.floor(
    isNumericString(row[DistributionParam.loc]) ? Number(row[DistributionParam.loc]) : 0,
  )

  const lowerBound = isNumericString(row[DistributionParam.lbound])
    ? Number(row[DistributionParam.lbound])
    : startIndex
  const upperBound = Number(row[DistributionParam.ubound])

  for (let index = startIndex; index <= upperBound; index++) {
    if (index < lowerBound) continue

    values.push(index.toString())
  }

  return values
}
